import { z } from 'zod';
import { decimalStringSchema } from './common';

/** Asset universe (API_DOCS.md — Asset Universe). */

/** 'BTC/USDT' — CCXT unified symbol notation, uppercase, always quoted in USDT. */
export const symbolSchema = z
  .string()
  .regex(/^[A-Z0-9]{2,15}\/[A-Z0-9]{2,10}$/, 'must be a unified symbol like "BTC/USDT"');

export const universeConfigSchema = z.object({
  topN: z.number().int().min(1).max(200),
  minQuoteVolume: decimalStringSchema,
  refreshMinutes: z.number().int().min(1).max(1440),
  /**
   * A market Binance newly opens is traded for this many days regardless of topN and the
   * volume floor, so a listing is never missed while it has no volume history. 0 = off.
   */
  newListingDays: z.number().int().min(0).max(30).default(3),
  /** How often the full Binance market list is checked for new listings, in seconds. */
  listingPollSec: z.number().int().min(30).max(3600).default(60),
});
export type UniverseConfig = z.infer<typeof universeConfigSchema>;

export const universeResponseSchema = z.object({
  config: universeConfigSchema,
  active: z.array(symbolSchema),
  whitelist: z.array(symbolSchema),
  blacklist: z.array(symbolSchema),
});
export type UniverseResponse = z.infer<typeof universeResponseSchema>;

export const symbolRequestSchema = z.object({ symbol: symbolSchema });
export type SymbolRequest = z.infer<typeof symbolRequestSchema>;

export const LISTING_STATUSES = ['upcoming', 'trading', 'removed'] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];

/** A USDT market the listing watcher saw appear on Binance. */
export interface ListingDto {
  readonly symbol: string;
  readonly base: string;
  readonly status: ListingStatus;
  /** When the watcher first saw the market (upcoming markets appear before they trade). */
  readonly firstSeenAt: string;
  /** When trading opened; null while upcoming. */
  readonly listedAt: string | null;
  /** Whether the bot is currently watching/trading it. */
  readonly inUniverse: boolean;
}

export interface ListingsResponse {
  /** Size of the tracked USDT market list — "all of Binance" for the dashboard. */
  readonly trackedMarkets: number;
  readonly lastCheckedAt: string | null;
  readonly items: ListingDto[];
}

export const universeRescanResponseSchema = z.object({
  active: z.array(symbolSchema),
  added: z.array(symbolSchema),
  removed: z.array(symbolSchema),
});
export type UniverseRescanResponse = z.infer<typeof universeRescanResponseSchema>;
